'use client';

import { Champignons } from '@repo/types';
import { SelectOption } from '@repo/ui/components/multiselect/index';
import { Label } from '@repo/ui/components/ui/label';
import { useMemo } from 'react';
import ReactSelect from 'react-select';

interface FungusSelectProps {
  champignons: Champignons[] | null;
  value: number[] | undefined;
  onChange: (value: number[]) => void;
  label: string;
  fieldId: string;
  placeholder?: string;
}

export function FungusSelect({
  champignons,
  value = [],
  onChange,
  label,
  fieldId,
  placeholder = "Sélectionnez des champignons..."
}: FungusSelectProps) {
  // Memoized fungus options
  const fungusOptions = useMemo<SelectOption<string>[]>(() => {
    return champignons?.map(champ => ({
      value: champ.id.toString(),
      label: champ.name || ''
    })) || [];
  }, [champignons]);

  // Memoized selected options for ReactSelect
  const selectedOptions = useMemo(() => {
    if (!Array.isArray(value)) {
      return [];
    }
    const selectedIds = value.map(id => id.toString());
    return fungusOptions.filter(opt => selectedIds.includes(opt.value));
  }, [value, fungusOptions]);

  const handleValueChange = (newValues: readonly SelectOption<string>[]) => {
    // Convert selected options back to number IDs
    const selectedIds = (newValues as SelectOption<string>[] || []).map(opt => Number(opt.value));
    onChange(selectedIds);
  };

  return (
    <div className="space-y-3">
      <Label htmlFor={fieldId} className="text-sm font-medium">
        {label}
      </Label>
      <ReactSelect<SelectOption<string>, true>
        options={fungusOptions}
        value={selectedOptions}
        onChange={handleValueChange}
        placeholder={placeholder}
        isMulti={true}
        isSearchable={true}
        isClearable={true}
        className="react-select-container"
        classNamePrefix="react-select"
        styles={{
          control: (base: any) => ({
            ...base,
            minHeight: '40px',
            border: '2px solid hsl(var(--input))',
            '&:hover': {
              border: '2px solid hsl(var(--primary) / 0.2)',
            },
          }),
          placeholder: (base: any) => ({
            ...base,
            color: 'hsl(var(--muted-foreground))',
          }),
        }}
      />
    </div>
  );
}
